import React, { useCallback, useContext, useEffect, useState } from 'react';

import MuiButton from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import {
  CLEVER_SSO_TEACHER_REDIRECT_URI,
  GOOGLE_CLASSROOM_LOGIN_REDIRECT_URI
} from 'config/constants';
import {
  EDLIGHT_MAIN_DOMAIN_URL,
  LOGIN_URL,
  TEACHERS_HOME_URL,
  TEACHER_ASSIGNMENTS_V2_URL,
  WALKTHROUGH_CREATE_PROFILE_URL
} from 'config/urls';
import logo from 'images/logo-dark.svg';
import { isUndefined } from 'lodash';
import { get, isNil, isNull, join } from 'lodash';
import mixpanel from 'mixpanel-browser';
import {
  completeProfile as completeInvitationProfile,
  updateUserPhoneNumber,
  useOnboardingUserSchool
} from 'sdk';

import { logout } from 'pages/Teachers/shared/TeacherPageLayout/components/TopBar/sdk';
import { Typography } from 'pages/Walkthrough/components';
import {
  LayoutWithBackgroundImage,
  ManualRostering,
  useWalkthroughUser
} from 'pages/Walkthrough/components';
import MeetingInvitationSelectSchool from 'pages/Walkthrough/components/RolloutForm/components/MeetingInvitationSelectSchool';
import { SIGN_UP_ONBOARDING_STAGES } from 'pages/Walkthrough/constants';
import { colors } from 'theme/palette';
import { buildFilters } from 'utils/filters';
import { openCleverLoginPage } from 'utils/integrations/clever';
import { openGoogleLoginPage } from 'utils/integrations/google';
import { shutdownIntercomWidget } from 'utils/integrations/intercom';
import { updateIntercomEntity } from 'utils/integrations/intercom';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import { default as pusherManager } from 'utils/integrations/pusher';
import { notifyError, notifyErrors } from 'utils/notifications';
import { useBreakpoints } from 'utils/resizing';
import { parseParams } from 'utils/urls';

import Image from 'components/Image';
import UserContext from 'components/UserContext';

import { StudentsLoadMethodChooser, TeacherDataForm } from './components';
import {
  completeWalkthroughProfile,
  createSchool,
  fetchMe,
  getOrCreateWalkthroughOrganization,
  getSchoolsList,
  getStatesList,
  updateUserPreferredName
} from './sdk';
import styles from './styles.module.scss';

const HOME_PAGE_ENABLED = process.env.REACT_APP_HOME_PAGE_ENABLED === 'true';

const RolloutForm = ({ history, location }) => {
  useWalkthroughUser(location.pathname);

  const searchParams = parseParams(history.location.search);

  const invitationIdentifier = get(searchParams, 'invitationIdentifier');
  const step = get(searchParams, 'activeStep');
  const schoolId = get(searchParams, 'schoolId');

  const { data: onboardingUserSchool } = useOnboardingUserSchool();

  const isMobile = useBreakpoints({
    tablet: 992,
    mobile: 670
  })[2];

  const { user, setUser } = useContext(UserContext);

  useEffect(() => {
    if (!user) {
      fetchMe().then(({ data, success }) => {
        if (success) {
          setUser(data);
        } else {
          notifyError('You need to create account first');
          history.push(WALKTHROUGH_CREATE_PROFILE_URL);
        }
      });
    }
  }, [user, setUser, history]);

  useEffect(() => {
    if (user) {
      updateIntercomEntity({ email: user.email });
    }
  }, [user]);

  const [activeStep, setActiveStep] = useState(0);

  const [preferredName, setPreferredName] = useState('');

  const [schools, setSchools] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedWalkthroughSchool, setSelectedWalkthroughSchool] =
    useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  const [isCompletingProfile, setIsCompletingProfile] = useState(false);

  const [selectedSchoolId, setSelectedSchoolId] = useState(null);

  const [showSelectFromMeetingSchools, setShowSelectFromMeetingSchools] =
    useState(!isNil(invitationIdentifier));

  useEffect(() => {
    if (user) {
      setPreferredName(user.name);
    }
  }, [user]);

  useEffect(() => {
    if (step) {
      setActiveStep(parseInt(step, 10));
    }
  }, [step]);

  useEffect(() => {
    if (schoolId || get(onboardingUserSchool, 'id')) {
      setSelectedSchoolId(
        parseInt(schoolId || get(onboardingUserSchool, 'id'))
      );
    }
  }, [schoolId, onboardingUserSchool]);

  useEffect(() => {
    if (selectedState) {
      getSchoolsList(buildFilters({ state: selectedState }))
        .then(({ data, success, errors }) => {
          if (success) {
            setSchools(data);
          } else {
            notifyErrors(errors);
          }
        })
        .catch(notifyErrors);
    }
  }, [selectedState]);

  useEffect(() => {
    getStatesList()
      .then(({ data, success, errors }) => {
        if (success) {
          setStates(data);
        } else {
          notifyErrors(errors);
        }
      })
      .catch(notifyErrors);
  }, []);

  useEffect(() => {
    if (isUndefined(process.env.REACT_APP_MIXPANEL_KEY) || isNil(user)) return;

    mixpanel.identify(String(user.id));
  }, [user]);

  useEffect(() => {
    if (!user) {
      return;
    }
    if (user.onboarding_stage === SIGN_UP_ONBOARDING_STAGES.PROFILE_COMPLETED) {
      setActiveStep(1);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (
      user.onboarding_stage === SIGN_UP_ONBOARDING_STAGES.CLASSES_IMPORTED ||
      user.onboarding_stage === SIGN_UP_ONBOARDING_STAGES.ACTIVATED
    ) {
      if (HOME_PAGE_ENABLED) {
        history.push(TEACHERS_HOME_URL);
      } else {
        history.push(TEACHER_ASSIGNMENTS_V2_URL);
      }
    }
  }, [user, history]);

  const logoutAndRedirect = async () => {
    shutdownIntercomWidget();
    pusherManager.disconnect();
    await logout().then(() => {
      history.push(LOGIN_URL);

      if (process.env.REACT_APP_MIXPANEL_KEY) {
        // Disassociate events from the user
        mixpanel.reset();
      }
    });
  };

  const refetchUser = useCallback(() => {
    fetchMe().then(({ data, success }) => {
      if (success) {
        setUser(data);
      }
    });
  }, [setUser]);

  const openGoogleClassroomLoginPage = () => {
    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'roster sync',
        view: 'complete',
        roster_type: 'google'
      }
    );

    const redirectUri = GOOGLE_CLASSROOM_LOGIN_REDIRECT_URI;

    const scope = join(
      [
        'https://www.googleapis.com/auth/userinfo.email',
        'https://www.googleapis.com/auth/userinfo.profile',
        'https://www.googleapis.com/auth/classroom.profile.emails',
        'https://www.googleapis.com/auth/classroom.courses.readonly',
        'https://www.googleapis.com/auth/classroom.rosters.readonly'
      ],
      ' '
    );

    const state = {
      schoolId: selectedSchoolId,
      userId: user.id,
      searchParams
    };

    openGoogleLoginPage(redirectUri, scope, state);
  };

  const openCleverSsoPage = () => {
    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'roster sync',
        view: 'complete',
        roster_type: 'clever'
      }
    );

    const redirectUri = CLEVER_SSO_TEACHER_REDIRECT_URI;
    const state = {
      schoolId: selectedSchoolId,
      userId: user.id,
      searchParams
    };

    openCleverLoginPage(redirectUri, state);
  };

  const openManualRosteringPage = () => {
    setActiveStep(2);
  };

  const ensureWalkthroughOrganization = async (values, orgData) => {
    const { data, success, errors } = await getOrCreateWalkthroughOrganization(
      orgData
    );

    if (success) {
      await createNewSchool(values, data.id);
    } else {
      notifyErrors(errors);
    }
  };

  const createNewSchool = async (values, walkthroughOrganizationId) => {
    const newSchoolData = {
      name: selectedWalkthroughSchool.name,
      state: values.stateAbbreviation,
      walkthrough_organization: walkthroughOrganizationId,
      city: values.city
    };

    const { data, success, errors } = await createSchool(newSchoolData);

    if (success) {
      await completeProfile(data.id, values.isSchoolAdmin);
    } else {
      notifyErrors(errors);
    }
  };

  const completeProfile = async (walkthroughSchoolId, isSchoolAdmin) => {
    const schoolData = {
      walkthrough_school: walkthroughSchoolId,
      is_school_admin: isSchoolAdmin
    };

    const { data, success, errors } = await completeWalkthroughProfile(
      schoolData
    );

    if (success) {
      setSelectedSchoolId(data.school_id);
      setActiveStep(1);
    } else {
      notifyErrors(errors);
    }
  };

  const handleInvitedMemberProfileComplete = useCallback(
    async ({ schoolId, preferredUserName }) => {
      setIsCompletingProfile(true);

      await updateUserPreferredName(user.id, {
        preferred_name: preferredUserName
      });

      const { success, errors } = await completeInvitationProfile({
        data: { school: schoolId }
      });

      if (success) {
        setSelectedSchoolId(schoolId);
        setActiveStep(1);
        setShowSelectFromMeetingSchools(false);
      } else {
        notifyErrors(errors);
      }
    },
    [user?.id]
  );

  const handleJoinDifferentSchool = useCallback(({ preferredName }) => {
    setPreferredName(preferredName);
    setShowSelectFromMeetingSchools(false);
  }, []);

  const onNextClick = async (values) => {
    setIsCompletingProfile(true);

    if (values.phoneNumber) {
      await updateUserPhoneNumber(user.id, {
        phone_number: values.phoneNumber
      });
    }

    await updateUserPreferredName(user.id, {
      preferred_name: values.preferredUserName
    });

    if (isNull(selectedWalkthroughSchool.id)) {
      await ensureWalkthroughOrganization(values, {
        organization: selectedOrganization.id,
        name: selectedOrganization.name
      });
    } else {
      await completeProfile(selectedWalkthroughSchool.id, values.isSchoolAdmin);
    }

    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`,
      {
        feature: 'sign up',
        view: 'complete',
        step: 'school info'
      }
    );

    setIsCompletingProfile(false);
  };

  return (
    <LayoutWithBackgroundImage>
      <div className={styles.page}>
        {!isMobile && (
          <a href={EDLIGHT_MAIN_DOMAIN_URL}>
            <Image src={logo} className={styles.logoSmall} alt="Logo link" />
          </a>
        )}

        <MuiButton className={styles.logInButton} onClick={logoutAndRedirect}>
          <Typography variant="H-TEXT-2" color={colors.blue2}>
            Log Out
          </Typography>
        </MuiButton>

        <div className={styles.container}>
          <Paper elevation={0} className={styles.paper}>
            <div className={styles.content}>
              <Image
                src={logo}
                className={styles.logoBig}
                alt="Logo placeholder"
              />

              {!isNil(invitationIdentifier) && showSelectFromMeetingSchools && (
                <MeetingInvitationSelectSchool
                  invitationIdentifier={invitationIdentifier}
                  isCompletingProfile={isCompletingProfile}
                  onJoinDifferentSchool={handleJoinDifferentSchool}
                  onNextClick={handleInvitedMemberProfileComplete}
                />
              )}

              {activeStep === 0 &&
                !showSelectFromMeetingSchools &&
                preferredName && (
                  <TeacherDataForm
                    schools={schools}
                    states={states}
                    selectedWalkthroughSchool={selectedWalkthroughSchool}
                    setSelectedWalkthroughSchool={setSelectedWalkthroughSchool}
                    setSelectedState={setSelectedState}
                    selectedOrganization={selectedOrganization}
                    setSelectedOrganization={setSelectedOrganization}
                    isCompletingProfile={isCompletingProfile}
                    onNextClick={onNextClick}
                    initalPreferredName={preferredName}
                  />
                )}
              {activeStep === 1 && (
                <StudentsLoadMethodChooser
                  loadViaGoogleClassroom={openGoogleClassroomLoginPage}
                  loadViaClever={openCleverSsoPage}
                  loadManuallyViaCsv={openManualRosteringPage}
                  invitationIdentifier={invitationIdentifier}
                  setActiveStep={setActiveStep}
                />
              )}
              {activeStep === 2 && (
                <ManualRostering
                  refetchUser={refetchUser}
                  selectedSchoolId={selectedSchoolId}
                  setActiveStep={setActiveStep}
                />
              )}
            </div>
          </Paper>
        </div>
      </div>
    </LayoutWithBackgroundImage>
  );
};

export default RolloutForm;
